/**
 * Marks for NATS connections the SDK closes on purpose (identity-flip drain,
 * recovery rebuild, handshake-watchdog orphan) so the `closed()` watcher in
 * `SDKProvider` can tell an expected close from the broker giving up on us.
 * Also records which connections have closed, so background loops (JetStream
 * bootstrap) stop against a dead socket instead of hammering it.
 *
 * `WeakSet`s pinned on `globalThis` so a dev hot-reload keeps the marks.
 */

import { noteConnectionDraining } from './connection-ledger.js';

const PIN_KEY = '__morfi_ncCloseMarks' as const;

interface CloseMarks {
  readonly intentional: WeakSet<object>;
  readonly closed: WeakSet<object>;
}

type GlobalWithMarks = typeof globalThis & { [PIN_KEY]?: CloseMarks };

function marks(): CloseMarks {
  const g = globalThis as GlobalWithMarks;
  if (g[PIN_KEY] === undefined) g[PIN_KEY] = { intentional: new WeakSet(), closed: new WeakSet() };
  return g[PIN_KEY];
}

/** Call BEFORE `drain()` / `close()` on a connection the SDK is retiring. */
export function markIntentionalClose(nc: object): void {
  marks().intentional.add(nc);
  noteConnectionDraining(nc);
}

export function wasIntentionalClose(nc: object): boolean {
  return marks().intentional.has(nc);
}

/** Recorded by the `closed()` watcher once the connection is gone for good. */
export function markClosed(nc: object): void {
  marks().closed.add(nc);
}

export function hasClosed(nc: object): boolean {
  return marks().closed.has(nc);
}
